import React, { useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    Stack,
    Avatar,
    useTheme,
    alpha,
    Grid
} from '@mui/material';

// Icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';

// Custom Components
import Header from '../components/Header';
import Footer from '../components/Footer';
import AiQuickMatch from '../components/AiQuickMatch'; // Importing the component

const AiLandingPage = () => {
    const theme = useTheme();
    // Reference to the AI Quick Match section for scrolling
    const quickMatchRef = useRef<HTMLDivElement>(null);

    // Smooth scroll handler for the "Start Finding" button
    const handleStartFinding = () => {
        if (quickMatchRef.current) {
            quickMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#FAFAFC' }}>
            <Header />

            {/* Main Hero Section */}
            <Box sx={{ flexGrow: 1, pt: { xs: 10, md: 14 }, pb: { xs: 6, md: 8 } }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        
                        {/* LEFT COLUMN: Text & Faded Truck Image */}
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ pr: { md: 4 } }}>
                                <Typography 
                                    variant="h2" 
                                    fontWeight={900} 
                                    gutterBottom 
                                    sx={{ color: '#111827', lineHeight: 1.15, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}
                                >
                                    Let AI find the perfect vehicle for your business
                                </Typography>
                                
                                <Typography 
                                    variant="h6" 
                                    color="text.secondary" 
                                    sx={{ fontWeight: 400, mb: 4, opacity: 0.9 }}
                                >
                                    Smart recommendations in less than 30 seconds.
                                </Typography>

                                <Button
                                    variant="contained"
                                    onClick={handleStartFinding}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        borderRadius: '50px',
                                        px: 4,
                                        py: 1.5,
                                        background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                                        color: '#fff',
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        boxShadow: '0 8px 20px rgba(255, 142, 0, 0.3)',
                                        '&:hover': { 
                                            background: 'linear-gradient(135deg, #FF8E00 0%, #E67E00 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 10px 25px rgba(255, 142, 0, 0.4)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Start Finding
                                </Button>

                                {/* The Faded Truck Image */}
                                <Box sx={{ mt: 6, width: '100%' }}>
                                    <Box 
                                        component="img"
                                        src="https://consumerstaging.workhorse.com.sg/MediaStorage/truck-ai-page.png" 
                                        alt="Workhorse Fleet"
                                        sx={{ 
                                            width: '100%', 
                                            maxWidth: '650px', 
                                            display: 'block',
                                            // CSS Masking creates the perfect gradient fade-out effect on the right side
                                            WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%)',
                                            maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%)',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        
                        {/* RIGHT COLUMN: The 3 Benefit Cards */}
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Stack spacing={3}>
                                
                                {/* Feature 1: Save Time */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: 'none', display: 'flex', gap: 2, bgcolor: alpha('#22c55e', 0.05) }}>
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#22c55e', border: '2px solid #22c55e', borderRadius: 2, width: 48, height: 48 }}>
                                        <AccessTimeIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={800} color="#111827">Save Time</Typography>
                                        <Typography variant="body2" color="text.secondary">AI finds the best options for you</Typography>
                                    </Box>
                                </Paper>

                                {/* Feature 2: Compare Easily */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: 'none', display: 'flex', gap: 2, bgcolor: alpha('#3b82f6', 0.05) }}>
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#3b82f6', border: '2px solid #3b82f6', borderRadius: 2, width: 48, height: 48 }}>
                                        <DashboardCustomizeIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={800} color="#111827">Compare Easily</Typography>
                                        <Typography variant="body2" color="text.secondary">Compare specs, prices & features</Typography>
                                    </Box>
                                </Paper>

                                {/* Feature 3: Better Decisions */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: 'none', display: 'flex', gap: 2, bgcolor: alpha('#a855f7', 0.05) }}>
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#a855f7', border: '2px solid #a855f7', borderRadius: 2, width: 48, height: 48 }}>
                                        <AutoGraphIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={800} color="#111827">Make Better Decisions</Typography>
                                        <Typography variant="body2" color="text.secondary">Data-driven insights you can trust</Typography>
                                    </Box>
                                </Paper>

                            </Stack>
                        </Grid>

                    </Grid>
                </Container>
            </Box>

            {/* AI Quick Match Component Section */}
            <Box 
                ref={quickMatchRef} 
                sx={{ 
                    width: '100%', 
                    bgcolor: '#ffffff', 
                    py: { xs: 6, md: 10 },
                    borderTop: '1px solid #f0f0f0'
                }}
            >
                <Container maxWidth="xl">
                    <AiQuickMatch />
                </Container>
            </Box>

            <Footer />
        </Box>
    );
};

export default AiLandingPage;